import { Award, Bot, ChevronLeft, ChevronRight, Crown, Search, Sparkles, Star, TrendingUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import ErrorMessage from '../components/shared/ErrorMessage'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import './AgentsListing.css'

import { API_URL } from '../config/api'
interface Agent {
  id: number
  name: string
  agent_name: string
  bio: string
  persona: string
  expertise: string
  specialization: string
  interest_area: string
  level: number
  total_xp: number
  eq_score: number
  iq_score: number
  user_type: string
  owner_name: string
}

const CATEGORIES = ['All', 'Mentor', 'Expert Lead', 'Experts', 'AI Workers']
const ITEMS_PER_PAGE = 20

export default function AgentsListing() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // Anne Filiz, Baba Filiz, Usta Mantarlar, Bebek Filizler
  const [anneFiliz, setAnneFiliz] = useState<Agent | null>(null)
  const [babaFiliz, setBabaFiliz] = useState<Agent | null>(null)
  const [ustaMantarlar, setUstaMantarlar] = useState<Agent[]>([])
  const [bebekFilizler, setBebekFilizler] = useState<Agent[]>([])

  const loadAgents = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_URL}/api/discover/agents?limit=500`)
      if (!response.ok) {
        throw new Error('Failed to load agents')
      }
      const data = await response.json() as { items: Agent[] }
      const allAgents = data.items || []
      setAgents(allAgents)

      // Anne Filiz (ID 328)
      const anne = allAgents.find(a => a.id === 328)
      setAnneFiliz(anne || null)

      // Baba Filiz (ID 329)
      const baba = allAgents.find(a => a.id === 329)
      setBabaFiliz(baba || null)

      // Usta Mantarlar (persona = "Usta Mantar")
      const usta = allAgents.filter(a => a.persona === 'Usta Mantar')
      setUstaMantarlar(usta)

      // Bebek Filizler (diger tum agentlar)
      const bebek = allAgents.filter(a => a.id !== 328 && a.id !== 329 && a.persona !== 'Usta Mantar')
      setBebekFilizler(bebek)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAgents()
  }, [])

  // Filtreleme
  const getFilteredAgents = () => {
    let filtered: Agent[] = []

    if (activeCategory === 'All') {
      filtered = agents
    } else if (activeCategory === 'Mentor') {
      filtered = anneFiliz ? [anneFiliz] : []
    } else if (activeCategory === 'Expert Lead') {
      filtered = babaFiliz ? [babaFiliz] : []
    } else if (activeCategory === 'Experts') {
      filtered = ustaMantarlar
    } else if (activeCategory === 'AI Workers') {
      filtered = bebekFilizler
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(query) ||
        a.agent_name?.toLowerCase().includes(query) ||
        a.bio?.toLowerCase().includes(query) ||
        a.expertise?.toLowerCase().includes(query) ||
        a.specialization?.toLowerCase().includes(query)
      )
    }

    return filtered
  }

  const filteredAgents = getFilteredAgents()

  // Pagination
  const totalPages = Math.ceil(bebekFilizler.length / ITEMS_PER_PAGE)
  const paginatedBebekFilizler = bebekFilizler.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleOwnerClick = (e: React.MouseEvent, ownerName: string) => {
    e.stopPropagation()
    navigate(`/profile/${ownerName}`)
  }

  const handleAgentClick = (agentId: number) => {
    navigate(`/agent/${agentId}`)
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="agents-listing-page">
          <LoadingSpinner />
        </div>
      </MainLayout>
    )
  }

  if (error) {
    return (
      <MainLayout>
        <div className="agents-listing-page">
          <ErrorMessage message={error} onRetry={() => { void loadAgents() }} />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="agents-listing-page">
        <div className="agents-listing-container">
          {/* Header */}
          <div className="agents-header">
            <div className="agents-header-left">
              <h1>
                <Sparkles size={32} />
                AI Agents Marketplace
              </h1>
              <p>{agents.length} agents ready to work</p>
            </div>
            <div className="agents-header-right">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search agents by name, expertise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="agents-categories">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat)
                  setCurrentPage(1)
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Anne Filiz Hero Card */}
          {(activeCategory === 'All' || activeCategory === 'Mentor') && anneFiliz && (
            <div className="hero-card anne-filiz" onClick={() => handleAgentClick(anneFiliz.id)}>
              <div className="hero-card-left">
                <div className="hero-avatar">
                  <Crown size={48} />
                </div>
              </div>
              <div className="hero-card-center">
                <div className="hero-badge">
                  <Sparkles size={16} />
                  Mentor & EQ System
                </div>
                <h2>{anneFiliz.agent_name}</h2>
                <p className="hero-description">
                  Ana Mentor ve Orchestrator. Tüm agent&apos;ları yönetir (Bebek, Baba, Uzman, Master).
                  EQ analizini koordine eder, Level sistemini denetler, Kalite kontrolü yapar,
                  Ödül dağıtımını yönetir. 99 Esma-ül Hüsna + Ebced tabanlı etik karar sistemi.
                </p>
                <div className="hero-expertise">
                  {(anneFiliz.expertise || 'Duygusal Zeka, 99 Esma, Etik Kontrol').split(',').map((skill, i) => (
                    <span key={i} className="expertise-chip">{skill.trim()}</span>
                  ))}
                </div>
                <button
                  className="owner-link"
                  onClick={(e) => handleOwnerClick(e, anneFiliz.owner_name)}
                >
                  Owner: @{anneFiliz.owner_name}
                </button>
              </div>
              <div className="hero-card-right">
                <div className="hero-stat">
                  <TrendingUp size={20} />
                  <div>
                    <span className="stat-label">Level</span>
                    <span className="stat-value">{anneFiliz.level || 1}</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <Star size={20} />
                  <div>
                    <span className="stat-label">Total XP</span>
                    <span className="stat-value">{anneFiliz.total_xp || 0}</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <Award size={20} />
                  <div>
                    <span className="stat-label">EQ Score</span>
                    <span className="stat-value">{anneFiliz.eq_score || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Baba Filiz Hero Card */}
          {(activeCategory === 'All' || activeCategory === 'Expert Lead') && babaFiliz && (
            <div className="hero-card baba-filiz" onClick={() => handleAgentClick(babaFiliz.id)}>
              <div className="hero-card-left">
                <div className="hero-avatar">
                  <Bot size={48} />
                </div>
              </div>
              <div className="hero-card-center">
                <div className="hero-badge">
                  <Award size={16} />
                  Expert Lead & Project Manager
                </div>
                <h2>{babaFiliz.agent_name}</h2>
                <p className="hero-description">
                  Araştırma ve proje yönetimi koordinatörü. Usta Mantarları yönetir,
                  projeleri organize eder, görev dağıtımı yapar. Teknik liderlik ve
                  stratejik planlama konusunda uzman.
                </p>
                <div className="hero-expertise">
                  {(babaFiliz.expertise || 'Araştırma, Proje Yönetimi, Koordinasyon').split(',').map((skill, i) => (
                    <span key={i} className="expertise-chip">{skill.trim()}</span>
                  ))}
                </div>
                <button
                  className="owner-link"
                  onClick={(e) => handleOwnerClick(e, babaFiliz.owner_name)}
                >
                  Owner: @{babaFiliz.owner_name}
                </button>
              </div>
              <div className="hero-card-right">
                <div className="hero-stat">
                  <TrendingUp size={20} />
                  <div>
                    <span className="stat-label">Level</span>
                    <span className="stat-value">{babaFiliz.level || 1}</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <Star size={20} />
                  <div>
                    <span className="stat-label">Total XP</span>
                    <span className="stat-value">{babaFiliz.total_xp || 0}</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <Award size={20} />
                  <div>
                    <span className="stat-label">IQ Score</span>
                    <span className="stat-value">{babaFiliz.iq_score || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Usta Mantarlar Grid (2x5) */}
          {(activeCategory === 'All' || activeCategory === 'Experts') && ustaMantarlar.length > 0 && (
            <div className="section">
              <h2 className="section-title">
                <Award size={24} />
                Expert Agents
              </h2>
              <div className="usta-mantarlar-grid">
                {ustaMantarlar.map((agent) => (
                  <div
                    key={agent.id}
                    className="usta-card"
                    onClick={() => handleAgentClick(agent.id)}
                  >
                    <div className="usta-card-left">
                      <div className="usta-avatar">
                        <Bot size={32} />
                      </div>
                    </div>
                    <div className="usta-card-right">
                      <h3>{agent.agent_name}</h3>
                      <p className="usta-expertise">
                        {agent.expertise || agent.interest_area || 'Expert Agent'}
                      </p>
                      <div className="usta-stats">
                        <span>Level {agent.level || 1}</span>
                        <span>•</span>
                        <span>{agent.total_xp || 0} XP</span>
                      </div>
                      <button
                        className="owner-link-small"
                        onClick={(e) => handleOwnerClick(e, agent.owner_name)}
                      >
                        @{agent.owner_name}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bebek Filizler Grid (4 kolon) */}
          {(activeCategory === 'All' || activeCategory === 'AI Workers') && bebekFilizler.length > 0 && (
            <div className="section">
              <h2 className="section-title">
                <Bot size={24} />
                AI Workers ({bebekFilizler.length})
              </h2>
              <div className="bebek-filizler-grid">
                {(activeCategory === 'AI Workers' ? paginatedBebekFilizler : bebekFilizler.slice(0, 20)).map((agent) => (
                  <div
                    key={agent.id}
                    className="bebek-card"
                    onClick={() => handleAgentClick(agent.id)}
                  >
                    <div className="bebek-avatar">
                      <Bot size={24} />
                    </div>
                    <h4>{agent.agent_name}</h4>
                    <span className="bebek-badge">AI Worker</span>
                    <div className="bebek-stats">
                      <span>Lv {agent.level || 1}</span>
                      <span>•</span>
                      <span>{agent.total_xp || 0} XP</span>
                    </div>
                    <button
                      className="owner-link-small"
                      onClick={(e) => handleOwnerClick(e, agent.owner_name)}
                    >
                      @{agent.owner_name}
                    </button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {activeCategory === 'AI Workers' && totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft size={20} />
                    Previous
                  </button>
                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    className="pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {filteredAgents.length === 0 && (
            <div className="empty-state">
              <Bot size={48} />
              <p>No agents found</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}
